<template>
  <div class="v2-cart-search-save-clear">
    <!-- Store Dropdown -->
    <div class="v2-cart-search-save-clear__row">
      <div class="v2-cart-search-save-clear__store-filter">
        <button
          class="v2-cart-search-save-clear__dropdown-toggle"
          :class="{ 'has-value': !!selectedStore.storeName && selectedStore.storeId !== null }"
          @click="toggleDropdown"
        >
          <span>{{ selectedStore.storeName || 'Select store' }}</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            :class="{ 'v2-cart-search-save-clear__chevron--open': isDropdownOpen }"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        <div
          v-if="isDropdownOpen"
          class="v2-cart-search-save-clear__dropdown-menu"
        >
          <div class="v2-cart-search-save-clear__dropdown-search">
            <input
              placeholder="Search"
              @input="onSearchStores($event.target.value)"
            >
          </div>
          <div
            v-if="!filteredStores.length"
            class="v2-cart-search-save-clear__dropdown-empty"
          >
            Nothing was found. Try another store name.
          </div>
          <button
            v-for="store in filteredStores"
            :key="store.storeId"
            class="v2-cart-search-save-clear__dropdown-item"
            :class="{ 'v2-cart-search-save-clear__dropdown-item--active': selectedStore.storeId === store.storeId }"
            @click="onSelectedStore(store)"
          >
            {{ store.storeName }}
          </button>
        </div>
        <!-- Backdrop to close dropdown -->
        <div
          v-if="isDropdownOpen"
          class="v2-cart-search-save-clear__backdrop"
          @click="isDropdownOpen = false"
        />
      </div>
    </div>

    <!-- Search + Buttons Row -->
    <div class="v2-cart-search-save-clear__actions-row">
      <!-- Product Search -->
      <div class="v2-cart-search-save-clear__search">
        <svg
          class="v2-cart-search-save-clear__search-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle
            cx="11"
            cy="11"
            r="8"
          />
          <line
            x1="21"
            y1="21"
            x2="16.65"
            y2="16.65"
          />
        </svg>
        <input
          placeholder="Search by products, style code..."
          @input="onSearchCartProducts($event.target.value)"
        >
      </div>

      <!-- Cart Buttons -->
      <div class="v2-cart-search-save-clear__buttons">
        <v2-button
          variant="destructive"
          size="md"
          :disabled="isSavingCart"
          @click="onClickClearCart"
        >
          <shopping-bag-deletete-icon class="v2-cart-search-save-clear__clear-icon" />
          Clear Cart
        </v2-button>
        <v2-button
          variant="outline"
          size="md"
          :disabled="isSavingCart"
          @click="onClickSaveCart"
        >
          Save Cart
        </v2-button>
      </div>
    </div>
  </div>
</template>

<script>
import V2Button from '@/components/v2/V2Button.vue'
import ShoppingBagDeleteteIcon from '@/@core/assets/svg-components/ShoppingBagDeleteteIcon.vue'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import { debounce } from 'lodash'

const CART_WITH_MULTIPLE_STORES = constants.TRACKS.ACTIONS.CART_WITH_MULTIPLE_STORES

const defaultStore = {
  storeName: 'All stores',
  storeId: null,
}

export default {
  name: 'V2CartSearchSaveClearSection',
  components: {
    V2Button,
    ShoppingBagDeleteteIcon,
  },
  props: {
    cartList: {
      type: Array,
      required: true,
    },
    isSavingCart: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['on-selected-store', 'on-search-cart-products', 'on-click-save-cart', 'on-click-clear-cart'],
  data() {
    return {
      stores: [defaultStore],
      filteredStores: [],
      selectedStore: defaultStore,
      isDropdownOpen: false,
    }
  },
  created() {
    const stores = this.cartList.map(cart => ({ storeName: cart?.storeName, storeId: cart?._id }))
    this.stores = [...this.stores, ...stores]
    this.filteredStores = this.stores
  },
  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
      this.trackStoreDropdown()
    },
    trackStoreDropdown() {
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_TOGGLES_STORES_LIST_ACCORDION, {
        carts: this.cartList,
      })
    },
    debouncedTrackSearchText: debounce((carts, searchInput) => {
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_SEARCHES_FOR_STORE, {
        carts,
        searchInput,
      })
    }, 500),
    onSearchStores(searchText) {
      if (!searchText) {
        this.filteredStores = this.stores
        return
      }
      this.filteredStores = this.stores.filter(store => store.storeName.toLowerCase().includes(searchText.toLowerCase()))
      this.debouncedTrackSearchText(this.cartList, searchText)
    },
    onSelectedStore(store) {
      this.selectedStore = store
      this.isDropdownOpen = false
      this.$emit('on-selected-store', store.storeId)
      if (store.storeId) {
        analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_SELECTS_STORE, {
          carts: this.cartList,
          storeId: store.storeId,
        })
      } else {
        analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_SELECTS_ALL_STORES, {
          carts: this.cartList,
        })
      }
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_CLICKED_FILTER_STORES_DROPDOWN, { selectedStore: store })
    },
    onSearchCartProducts(searchText) {
      this.$emit('on-search-cart-products', searchText)
    },
    onClickSaveCart() {
      this.$emit('on-click-save-cart')
    },
    onClickClearCart() {
      this.$emit('on-click-clear-cart')
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-cart-search-save-clear {
  display: flex;
  flex-direction: column;
  gap: var(--v2-space-3);

  &__row {
    width: 100%;
  }

  &__store-filter {
    position: relative;
    width: 100%;
  }

  /* Glass dropdown toggle — matches V2Dropdown recipe. */
  &__dropdown-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    background: var(--v2-glass-surface-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
      border-color: var(--v2-glass-stroke-strong);
    }

    &.has-value {
      color: var(--v2-foreground);
    }
  }

  &__chevron--open {
    transform: rotate(180deg);
  }

  &__backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 9;
  }

  /* Floating glass — matches V2Dropdown menu recipe. */
  &__dropdown-menu {
    position: absolute;
    top: calc(100% + var(--v2-space-1));
    left: 0;
    right: 0;
    background: var(--v2-glass-surface-floating);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    box-shadow: var(--v2-glass-shadow-floating);
    max-height: 260px;
    overflow-y: auto;
    z-index: 10;
  }

  &__dropdown-search {
    padding: var(--v2-space-2);
    border-bottom: 1px solid var(--v2-border);

    input {
      width: 100%;
      padding: var(--v2-space-2) var(--v2-space-3);
      border: 1px solid var(--v2-border);
      border-radius: var(--v2-radius-md);
      font-family: var(--v2-font-sans);
      font-size: 0.875rem;
      color: var(--v2-foreground);
      outline: none;

      &:focus {
        border-color: var(--v2-primary);
      }

      &::placeholder {
        color: var(--v2-muted-foreground);
      }
    }
  }

  &__dropdown-empty {
    padding: var(--v2-space-3);
    text-align: center;
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
  }

  &__dropdown-item {
    display: block;
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    background: transparent;
    border: none;
    text-align: left;
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    cursor: pointer;
    transition: background 0.1s ease;

    &:hover {
      background: var(--v2-muted);
    }

    &--active {
      background: var(--v2-muted);
      color: var(--v2-primary);
      font-weight: 500;
    }
  }

  &__actions-row {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
  }

  &__search {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;

    /* Glass search input — matches V2Input recipe. */
    input {
      width: 100%;
      padding: var(--v2-space-2) var(--v2-space-3) var(--v2-space-2) var(--v2-space-8);
      border: 1px solid var(--v2-glass-stroke);
      border-radius: var(--v2-radius-md);
      font-family: var(--v2-font-sans);
      font-size: 0.875rem;
      color: var(--v2-foreground);
      background: var(--v2-glass-surface-2);
      outline: none;
      transition: background 0.15s ease, border-color 0.15s ease;

      &:hover:not(:focus) {
        border-color: var(--v2-glass-stroke-strong);
      }

      &:focus {
        border-color: var(--v2-primary);
      }

      &::placeholder {
        color: var(--v2-muted-foreground);
      }
    }
  }

  &__search-icon {
    position: absolute;
    left: var(--v2-space-3);
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    color: var(--v2-muted-foreground);
    pointer-events: none;
  }

  &__buttons {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    flex-shrink: 0;
  }

  &__clear-icon {
    vertical-align: bottom;
  }
}
</style>
